import { Attachment01 } from '@untitled-ui/icons-react'
import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import classNames from 'classnames'
import dayjs from 'dayjs'

import GorgiasAiSummary from '@/components/gorgias/GorgiasAiSummary'

// --- Rendu HTML (mode "html") ---
// Extrait le contenu du <body> et normalise les sauts de ligne du HTML Gorgias.
const extractBodyContent = (html) => {
    let unescaped = html.replace(/\\n/g, '\n')
    const match = unescaped.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
    let body = match ? match[1] : unescaped
    body = body.replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '[[BR]]')
    body = body.replace(/<div>\s*<\/div>/gi, '')
    body = body.replace(/(<br\s*\/?>\s*){2,}/gi, '<br />')
    body = body.replace(/\[\[BR\]\]/g, '<br />')
    body = body.replace(/>\s+</g, '><').trim()
    return body
}

// --- Rendu texte (mode "text") ---
// Convertit le HTML Gorgias en texte brut lisible.
const toPlainText = (raw) => {
    if (!raw) return ''
    let s = raw.replace(/\\n/g, '\n')
    s = s.replace(/<br\s*\/?>/gi, '\n')
    s = s.replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    s = s.replace(/<[^>]+>/g, '')

    const el = document.createElement('textarea')
    el.innerHTML = s
    s = el.value

    return s
        .replace(/ /g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

const getInitials = (name, email) => {
    if (name) {
        return name
            .split(' ')
            .slice(0, 2)
            .map((n) => n[0])
            .join('')
            .toUpperCase()
    }
    return email?.[0]?.toUpperCase() || '?'
}

const MessageBody = ({ message, mode }) => {
    if (mode === 'html') {
        const html = extractBodyContent(message.stripped_text || '')
        const cleanHtml = DOMPurify.sanitize(html, {
            USE_PROFILES: { html: true },
        })
        const parsedContent = parse(cleanHtml, {
            replace: (domNode) => {
                if (domNode.name === 'a' && domNode.attribs?.href) {
                    domNode.attribs.target = '_blank'
                    domNode.attribs.rel = 'noopener noreferrer'
                }
            },
        })

        return (
            <div className="text-sm text-zinc-700 leading-relaxed gorgias-message whitespace-pre-wrap">
                {parsedContent}
            </div>
        )
    }

    return (
        <div className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap wrap-break-word">
            {toPlainText(message.stripped_text || '')}
        </div>
    )
}

const Message = ({ message, mode }) => {
    const isAgent = message.from_agent
    const isPrivate = !message.public
    const senderName = message.sender?.name || ''
    const senderEmail = message.sender?.email || ''
    const displayName =
        message.sender?.firstname && message.sender?.lastname
            ? `${message.sender.firstname} ${message.sender.lastname}`
            : senderName || senderEmail

    return (
        <div
            className={classNames(
                'flex',
                isAgent ? 'justify-end' : 'justify-start'
            )}
        >
            <div
                className={classNames(
                    'rounded-2xl border px-4 py-3 max-w-[85%]',
                    isPrivate
                        ? 'bg-amber-50 border-amber-200'
                        : isAgent
                          ? 'bg-blue-50/40 border-blue-100/70'
                          : 'bg-white border-zinc-200'
                )}
            >
            {/* En-tête dans la bulle : avatar · nom · email · horodatage */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div
                    className={classNames(
                        'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 select-none',
                        isPrivate
                            ? 'bg-amber-100 text-amber-700'
                            : isAgent
                              ? 'bg-zinc-800 text-white'
                              : 'bg-zinc-200 text-zinc-600'
                    )}
                >
                    {getInitials(senderName, senderEmail)}
                </div>

                <span className="text-xs font-semibold text-zinc-800">
                    {displayName}
                </span>
                {senderEmail && senderEmail !== displayName && (
                    <span className="text-[11px] text-zinc-400 truncate">
                        {senderEmail}
                    </span>
                )}
                {isPrivate && (
                    <span className="text-[10px] text-amber-600 font-medium">
                        Note interne
                    </span>
                )}
                <span className="text-[11px] text-zinc-400 ml-auto shrink-0">
                    {dayjs(message.date).format('D MMM YYYY · HH:mm')}
                </span>
            </div>

            <MessageBody message={message} mode={mode} />

            {message.attachments?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {message.attachments.map((attachment) => (
                        <a
                            key={attachment.url}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-800 bg-zinc-200/70 hover:bg-zinc-300/70 transition-colors rounded-sm px-2 py-1"
                        >
                            <Attachment01 width={11} height={11} />
                            {attachment.name}
                        </a>
                    ))}
                </div>
            )}
            </div>
        </div>
    )
}

/**
 * Affiche un fil de conversation Gorgias (messages + résumé IA optionnel).
 * Partagé entre la page support et le SAV.
 * @param {('text'|'html')} mode - rendu du corps des messages
 */
export default function GorgiasConversation({ messages = [], ai, mode = 'text' }) {
    return (
        <div className="flex flex-col gap-5">
            <GorgiasAiSummary ai={ai} className="mb-1" />

            {messages.map((message) => (
                <Message key={message.id} message={message} mode={mode} />
            ))}
        </div>
    )
}
